import { create } from 'zustand';

interface LoadingState {
  // Global loading state
  globalLoading: boolean;
  globalMessage?: string | null;
  globalProgress?: number | null;
  
  // Individual loading states
  loadingStates: Record<string, {
    loading: boolean;
    message?: string | null;
    progress?: number | null;
    startTime: Date;
  }>;
  
  // Actions
  setGlobalLoading: (loading: boolean, message?: string | null, progress?: number | null) => void;
  setLoading: (key: string, loading: boolean, message?: string | null, progress?: number | null) => void;
  updateProgress: (key: string, progress: number) => void;
  clearLoading: (key?: string) => void;
  clearAllLoading: () => void;
  
  // Getters
  isLoading: (key?: string) => boolean;
  getLoadingState: (key: string) => { loading: boolean; message?: string | null; progress?: number | null; duration?: number } | null;
  getAllLoadingStates: () => Array<{ key: string; loading: boolean; message?: string | null; progress?: number | null; duration: number }>;
}

export const useLoadingStore = create<LoadingState>((set, get) => ({
  globalLoading: false,
  globalMessage: null,
  globalProgress: null,
  loadingStates: {},
  
  setGlobalLoading: (loading: boolean, message?: string | null, progress?: number | null) => {
    set((state) => {
      const newState: Partial<LoadingState> = {
        globalLoading: loading
      };
      
      if (message !== undefined) {
        newState.globalMessage = message;
      } else if (!loading) {
        newState.globalMessage = null;
      }
      
      if (progress !== undefined) {
        newState.globalProgress = progress;
      } else if (!loading) {
        newState.globalProgress = null;
      }
      
      return { ...state, ...newState };
    });
  },
  
  setLoading: (key: string, loading: boolean, message?: string | null, progress?: number | null) => {
    set((state) => {
      const newLoadingStates = { ...state.loadingStates };
      
      if (loading) {
        const newState: { loading: boolean; startTime: Date; message?: string | null; progress?: number | null } = {
          loading: true,
          startTime: new Date()
        };
        if (message !== undefined) newState.message = message;
        if (progress !== undefined) newState.progress = progress;
        newLoadingStates[key] = newState;
      } else {
        delete newLoadingStates[key];
      }
      
      return { loadingStates: newLoadingStates };
    });
  },
  
  updateProgress: (key: string, progress: number) => {
    set((state) => {
      const currentState = state.loadingStates[key];
      if (!currentState) return state;
      
      return {
        loadingStates: {
          ...state.loadingStates,
          [key]: {
            ...currentState,
            progress
          }
        }
      };
    });
  },
  
  clearLoading: (key?: string) => {
    if (key) {
      set((state) => {
        const newLoadingStates = { ...state.loadingStates };
        delete newLoadingStates[key];
        return { loadingStates: newLoadingStates };
      });
    } else {
      set((state) => ({
        ...state,
        globalLoading: false,
        globalMessage: null,
        globalProgress: null
      }));
    }
  },
  
  clearAllLoading: () => {
    set((state) => ({
      ...state,
      globalLoading: false,
      globalMessage: null,
      globalProgress: null,
      loadingStates: {}
    }));
  },
  
  isLoading: (key?: string) => {
    const state = get();
    if (key) {
      const loadingState = state.loadingStates[key];
      return loadingState && Object.prototype.hasOwnProperty.call(state.loadingStates, key) ? loadingState.loading : false;
    }
    
    // Check if global loading is active
    if (state.globalLoading) {
      return true;
    }
    
    // Check if any individual loading states are active
    return Object.keys(state.loadingStates).some(k => 
      Object.prototype.hasOwnProperty.call(state.loadingStates, k) && state.loadingStates[k]?.loading
    );
  },
  
  getLoadingState: (key: string) => {
    const state = get();
    const loadingState = state.loadingStates[key];
    // Check if the key actually exists in our loading states (not inherited properties)
    if (!loadingState || !Object.prototype.hasOwnProperty.call(state.loadingStates, key)) {
      return null;
    }
    
    const duration = loadingState.startTime ? Date.now() - loadingState.startTime.getTime() : 0;
    const result: { loading: boolean; duration: number; message?: string | null; progress?: number | null } = {
      loading: loadingState.loading,
      duration
    };
    if (loadingState.message !== undefined) result.message = loadingState.message;
    if (loadingState.progress !== undefined) result.progress = loadingState.progress;
    return result;
  },
  
  getAllLoadingStates: () => {
    const state = get();
    return Object.keys(state.loadingStates)
      .filter(key => Object.prototype.hasOwnProperty.call(state.loadingStates, key))
      .map(key => {
        const loadingState = state.loadingStates[key]!;
        const duration = loadingState.startTime ? Date.now() - loadingState.startTime.getTime() : 0;
        const result: { key: string; loading: boolean; duration: number; message?: string | null; progress?: number | null } = {
          key,
          loading: loadingState.loading,
          duration
        };
        if (loadingState.message !== undefined) result.message = loadingState.message;
        if (loadingState.progress !== undefined) result.progress = loadingState.progress;
        return result;
      });
  }
}));

// Export the store for direct access in tests
export const loadingStore = {
  setLoading: (key: string, loading: boolean, message?: string | null, progress?: number | null) => 
    useLoadingStore.getState().setLoading(key, loading, message, progress),
  isLoading: (key?: string) => useLoadingStore.getState().isLoading(key),
  getLoadingMessage: (key: string) => useLoadingStore.getState().getLoadingState(key)?.message,
  clearAll: () => useLoadingStore.getState().clearAllLoading(),
  setGlobalLoading: (loading: boolean, message?: string | null, progress?: number | null) =>
    useLoadingStore.getState().setGlobalLoading(loading, message, progress),
  isGlobalLoading: () => useLoadingStore.getState().globalLoading
};